import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, Save, Trash2, X } from 'lucide-react';
import { Client, ClientFormData } from '../../types';
import { LocationFormSection } from '../forms/LocationFormSection';
import { CustomSelect } from '../common/CustomSelect';

interface ClientFormModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: ClientFormData) => Promise<void>;
	client?: Client | null;
	loading: boolean;
}

export const ClientFormModal: React.FC<ClientFormModalProps> = ({ isOpen, onClose, onSubmit, client, loading }) => {
	const [formData, setFormData] = useState<ClientFormData>({
		name: '',
		email: '',
		apiKey: '',
		gpClientId: '',
		gpClientSecret: '',
		type: 'ZOHO_BOOKS',
		config: { region: 'COM' },
		locations: [{ name: '', gpLocationId: '', targetLocationId: null, isHeadOffice: true }],
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (client) {
			const clientData = (client as any).client || client;
			setFormData({
				name: clientData.name || '',
				email: clientData.email || '',
				apiKey: clientData.apiKey || '',
				gpClientId: clientData.gpClientId || '',
				gpClientSecret: clientData.gpClientSecret || '',
				type: clientData.type || 'ZOHO_BOOKS',
				config:
					clientData.type === 'ODOO_FINANCE'
						? {
								baseUrl: clientData.config?.odooBaseUrl,
								database: clientData.config?.odooDatabase,
								username: clientData.config?.odooUsername,
								apiKey: clientData.config?.odooApiKey,
								automationIds: clientData.config?.odooAutomationIds ?? [],
							}
						: clientData.type === 'MSD_FINANCE'
							? {
									resource: clientData.config?.msdResource,
									tenantId: clientData.config?.msdTenantId,
									clientId: clientData.config?.msdClientId,
									clientSecret: clientData.config?.msdClientSecret,
									dataAreaId: clientData.config?.msdDataAreaId,
									serviceBusConnectionString: clientData.config?.msdServiceBusConnectionString,
									serviceBusQueueName: clientData.config?.msdServiceBusQueueName,
								}
							: {
									// Zoho
									clientId: clientData.config?.zohoClientId,
									clientSecret: clientData.config?.zohoClientSecret,
									region: clientData.config?.zohoRegion ?? 'COM',
								},
				locations: clientData.locations?.length
					? clientData.locations
					: [{ name: '', gpLocationId: '', targetLocationId: '', isHeadOffice: true }],
			});
		} else {
			setFormData({
				name: '',
				email: '',
				apiKey: '',
				gpClientId: '',
				gpClientSecret: '',
				type: 'ZOHO_BOOKS',
				config: { region: 'COM' },
				locations: [{ name: '', gpLocationId: '', targetLocationId: null, isHeadOffice: true }],
			});
		}
	}, [client]);

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.name.trim()) newErrors.name = 'Name is required';
		if (!formData.email.trim()) newErrors.email = 'Email is required';
		if (!formData.email.includes('@')) newErrors.email = 'Valid email required';
		if (!formData.apiKey.trim()) newErrors.apiKey = 'GaragePlug API Key is required';
		if (!formData.gpClientId.trim()) {
			newErrors.gpClientId = 'GaragePlug Client ID is required';
		}
		if (!formData.gpClientSecret.trim()) {
			newErrors.gpClientSecret = 'GaragePlug Client Secret is required';
		}

		// Location validation
		if (!formData.locations || formData.locations.length === 0) {
			newErrors.locations = 'At least one location is required';
		} else {
			const gpLocationIds = new Set();
			let headOfficeCount = 0;
			formData.locations.forEach((location, index) => {
				if (!location.name?.trim()) {
					newErrors[`location_${index}_name`] = 'Location name is required';
				}
				if (!location.gpLocationId?.trim()) {
					newErrors[`location_${index}_gpLocationId`] = 'GP Location ID is required';
				} else if (gpLocationIds.has(location.gpLocationId)) {
					newErrors[`location_${index}_gpLocationId`] = 'GP Location ID must be unique';
				} else {
					gpLocationIds.add(location.gpLocationId);
				}
				if (!location.isHeadOffice && !location.targetLocationId?.trim()) {
					newErrors[`location_${index}_targetLocationId`] = 'Target Location ID is required';
				}
				if (location.isHeadOffice) headOfficeCount++;
			});
			if (headOfficeCount === 0) {
				newErrors.locations_headoffice = 'Exactly one location must be marked as Head Office';
			} else if (headOfficeCount > 1) {
				newErrors.locations_headoffice = 'Only one location can be marked as Head Office';
			}
		}

		// Integration-specific validation
		if (formData.type === 'ZOHO_BOOKS') {
			if (!formData.config.clientId?.trim()) {
				newErrors.zohoClientId = 'Zoho Client ID is required';
			}
			if (!formData.config.clientSecret?.trim()) {
				newErrors.zohoClientSecret = 'Zoho Client Secret is required';
			}
			if (!formData.config.region?.trim()) {
				newErrors.zohoRegion = 'Zoho Region is required';
			}
		}

		if (formData.type === 'ODOO_FINANCE') {
			if (!formData.config.baseUrl?.trim()) {
				newErrors.odooBaseUrl = 'Odoo Base URL is required';
			}
			if (!formData.config.database?.trim()) {
				newErrors.odooDatabase = 'Odoo Database is required';
			}
			if (!formData.config.username?.trim()) {
				newErrors.odooUsername = 'Odoo Username is required';
			}
			if (!formData.config.apiKey?.trim()) {
				newErrors.odooApiKey = 'Odoo API Key is required';
			}
			const ids = (formData.config.automationIds as string[]) ?? [];
			const filledIds = ids.filter((id) => id.trim() !== '');
			if (filledIds.length === 0) {
				newErrors.odooAutomationIds = 'At least one Automation ID is required';
			} else if (filledIds.some((id) => !/^\d+$/.test(id.trim()))) {
				newErrors.odooAutomationIds = 'Automation IDs must be numeric (e.g. 743)';
			}
		}

		if (formData.type === 'MSD_FINANCE') {
			if (!formData.config.clientId?.trim()) {
				newErrors.msdClientId = 'MSD Client ID is required';
			}
			if (!formData.config.clientSecret?.trim()) {
				newErrors.msdClientSecret = 'MSD Client Secret is required';
			}
			if (!formData.config.tenantId?.trim()) {
				newErrors.msdTenantId = 'MSD Tenant ID is required';
			}
			if (!formData.config.resource?.trim()) {
				newErrors.msdResource = 'MSD Resource URL is required';
			}
			if (!formData.config.dataAreaId?.trim()) {
				newErrors.msdDataAreaId = 'MSD Legal Entity (Data Area ID) is required';
			}
			if (!formData.config.serviceBusConnectionString?.trim()) {
				newErrors.msdServiceBusConnectionString = 'Service Bus Connection String is required';
			}
			if (!formData.config.serviceBusQueueName?.trim()) {
				newErrors.msdServiceBusQueueName = 'Service Bus Queue Name is required';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (validateForm()) {
			await onSubmit(formData);
		}
	};

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: '' }));
		}
	};

	const handleConfigChange = (field: string, value: string | string[]) => {
		setFormData((prev) => ({
			...prev,
			config: { ...prev.config, [field]: value },
		}));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: '' }));
		}
	};

	const handleLocationsChange = (locations: any[]) => {
		setFormData((prev) => ({ ...prev, locations }));
		// Clear locations error when locations are added
		if (errors.locations && locations.length > 0) {
			setErrors((prev) => ({ ...prev, locations: '' }));
		}
	};

	const handleErrorChange = (field: string, error: string) => {
		setErrors((prev) => ({ ...prev, [field]: error }));
	};

	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[55] p-4">
			<div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
				<div className="flex items-center justify-between p-6 border-b">
					<h2 className="text-xl font-semibold text-gray-900">{client ? 'Edit Client' : 'Add New Client'}</h2>
					<button onClick={onClose} className="text-gray-400 hover:text-gray-600">
						<X className="h-6 w-6" />
					</button>
				</div>

				<form onSubmit={handleSubmit} className="p-6 space-y-6">
					{/* Basic Information */}
					<div className="space-y-4">
						<h3 className="text-lg font-medium text-gray-900">Basic Information</h3>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
								<input
									type="text"
									value={formData.name}
									onChange={(e) => handleInputChange('name', e.target.value)}
									className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
										errors.name ? 'border-red-300' : 'border-gray-300'
									}`}
									placeholder="Enter client name"
								/>
								{errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
								<input
									type="email"
									value={formData.email}
									onChange={(e) => handleInputChange('email', e.target.value)}
									className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
										errors.email ? 'border-red-300' : 'border-gray-300'
									}`}
									placeholder="client@example.com"
								/>
								{errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
							</div>
						</div>
					</div>

					{/* GaragePlug Configuration */}
					<div className="space-y-4">
						<h3 className="text-lg font-medium text-gray-900">GaragePlug Configuration</h3>

						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">GaragePlug API Key *</label>
								<input
									type="text"
									value={formData.apiKey}
									onChange={(e) => handleInputChange('apiKey', e.target.value)}
									className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
										errors.apiKey ? 'border-red-300' : 'border-gray-300'
									}`}
									placeholder="Enter GaragePlug API Key"
								/>
								{errors.apiKey && <p className="text-red-500 text-sm mt-1">{errors.apiKey}</p>}
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">GaragePlug Client ID *</label>
									<input
										type="text"
										value={formData.gpClientId}
										onChange={(e) => handleInputChange('gpClientId', e.target.value)}
										className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
											errors.gpClientId ? 'border-red-300' : 'border-gray-300'
										}`}
										placeholder="Enter GaragePlug Client ID"
									/>
									{errors.gpClientId && <p className="text-red-500 text-sm mt-1">{errors.gpClientId}</p>}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">GaragePlug Client Secret *</label>
									<input
										type="text"
										value={formData.gpClientSecret}
										onChange={(e) => handleInputChange('gpClientSecret', e.target.value)}
										className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
											errors.gpClientSecret ? 'border-red-300' : 'border-gray-300'
										}`}
										placeholder="Enter GaragePlug Client Secret"
									/>
									{errors.gpClientSecret && <p className="text-red-500 text-sm mt-1">{errors.gpClientSecret}</p>}
								</div>
							</div>
						</div>
					</div>

					{/* Location Management */}
					<LocationFormSection
						locations={formData.locations}
						onLocationsChange={handleLocationsChange}
						errors={errors}
						onErrorChange={handleErrorChange}
					/>

					{/* Integration Type */}
					<div className="space-y-4">
						<h3 className="text-lg font-medium text-gray-900">Integration Type</h3>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Integration Type *</label>
							<CustomSelect
								value={formData.type}
								onChange={(value) => handleInputChange('type', value)}
								options={[
									{ value: 'ZOHO_BOOKS', label: 'Zoho Books' },
									{ value: 'ODOO_FINANCE', label: 'Odoo Finance' },
									{
										value: 'MSD_FINANCE',
										label: 'Microsoft Dynamics 365 Finance',
									},
								]}
							/>
						</div>
					</div>

					{/* Integration Configuration */}
					<div className="space-y-4">
						<h3 className="text-lg font-medium text-gray-900">
							{formData.type === 'ZOHO_BOOKS'
								? 'Zoho Books'
								: formData.type === 'ODOO_FINANCE'
									? 'Odoo Finance'
									: 'Microsoft Dynamics 365 Finance'}{' '}
							Configuration
						</h3>

						{formData.type === 'ZOHO_BOOKS' ? (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Zoho Client ID *</label>
									<input
										type="text"
										value={formData.config.clientId || ''}
										onChange={(e) => handleConfigChange('clientId', e.target.value)}
										className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
											errors.zohoClientId ? 'border-red-300' : 'border-gray-300'
										}`}
										placeholder="Enter Zoho Client ID"
									/>
									{errors.zohoClientId && <p className="text-red-500 text-sm mt-1">{errors.zohoClientId}</p>}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Zoho Client Secret *</label>
									<input
										type="text"
										value={formData.config.clientSecret || ''}
										onChange={(e) => handleConfigChange('clientSecret', e.target.value)}
										className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
											errors.zohoClientSecret ? 'border-red-300' : 'border-gray-300'
										}`}
										placeholder="Enter Zoho Client Secret"
									/>
									{errors.zohoClientSecret && <p className="text-red-500 text-sm mt-1">{errors.zohoClientSecret}</p>}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Zoho Region *</label>
									<CustomSelect
										value={formData.config.region || 'COM'}
										onChange={(value) => handleConfigChange('region', value)}
										options={[
											{ value: 'COM', label: 'COM – United States' },
											{ value: 'EU', label: 'EU – Europe' },
											{ value: 'IN', label: 'IN – India' },
											{ value: 'AU', label: 'AU – Australia' },
											{ value: 'JP', label: 'JP – Japan' },
											{ value: 'CA', label: 'CA – Canada' },
											{ value: 'SA', label: 'SA – Saudi Arabia' },
											{ value: 'CN', label: 'CN – China' },
										]}
									/>
									{errors.zohoRegion && <p className="text-red-500 text-sm mt-1">{errors.zohoRegion}</p>}
								</div>
							</div>
						) : formData.type === 'ODOO_FINANCE' ? (
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Odoo Base URL *</label>
									<input
										type="url"
										value={formData.config.baseUrl || ''}
										onChange={(e) => handleConfigChange('baseUrl', e.target.value)}
										className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
											errors.odooBaseUrl ? 'border-red-300' : 'border-gray-300'
										}`}
										placeholder="https://your-instance.odoo.com"
									/>
									{errors.odooBaseUrl && <p className="text-red-500 text-sm mt-1">{errors.odooBaseUrl}</p>}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Database Name *</label>
									<input
										type="text"
										value={formData.config.database || ''}
										onChange={(e) => handleConfigChange('database', e.target.value)}
										className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
											errors.odooDatabase ? 'border-red-300' : 'border-gray-300'
										}`}
										placeholder="Enter Odoo database name"
									/>
									{errors.odooDatabase && <p className="text-red-500 text-sm mt-1">{errors.odooDatabase}</p>}
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
										<input
											type="text"
											value={formData.config.username || ''}
											onChange={(e) => handleConfigChange('username', e.target.value)}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
												errors.odooUsername ? 'border-red-300' : 'border-gray-300'
											}`}
											placeholder="Enter Odoo username"
										/>
										{errors.odooUsername && <p className="text-red-500 text-sm mt-1">{errors.odooUsername}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">API Key *</label>
										<input
											type="text"
											value={formData.config.apiKey || ''}
											onChange={(e) => handleConfigChange('apiKey', e.target.value)}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
												errors.odooApiKey ? 'border-red-300' : 'border-gray-300'
											}`}
											placeholder="Enter Odoo API key"
										/>
										{errors.odooApiKey && <p className="text-red-500 text-sm mt-1">{errors.odooApiKey}</p>}
									</div>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Automation IDs *</label>
									<p className="text-xs text-gray-500 mb-2">
										From Odoo: Settings → Technical → Automation → Automated Actions — use the numeric ID from the URL
									</p>
									<div className="space-y-2">
										{((formData.config.automationIds as string[]) ?? []).map((id: string, index: number) => (
											<div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2">
												<div className="md:col-span-2">
													<input
														type="text"
														value={id}
														onChange={(e) => {
															const updated = [...((formData.config.automationIds as string[]) ?? [])];
															updated[index] = e.target.value;
															handleConfigChange('automationIds', updated);
														}}
														className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm ${
															errors.odooAutomationIds ? 'border-red-300' : 'border-gray-300'
														}`}
														placeholder="e.g. 743"
													/>
												</div>
												<button
													type="button"
													onClick={() => {
														const updated = ((formData.config.automationIds as string[]) ?? []).filter(
															(_: string, i: number) => i !== index,
														);
														handleConfigChange('automationIds', updated);
													}}
													className="px-3 py-2 text-red-500 hover:text-red-700 border border-gray-300 rounded-lg hover:bg-red-50 flex items-center justify-center">
													<Trash2 className="h-4 w-4" />
												</button>
											</div>
										))}
										<button
											type="button"
											onClick={() =>
												handleConfigChange('automationIds', [
													...((formData.config.automationIds as string[]) ?? []),
													'',
												])
											}
											className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
											+ Add Automation ID
										</button>
									</div>
									{errors.odooAutomationIds && <p className="text-red-500 text-sm mt-1">{errors.odooAutomationIds}</p>}
								</div>
							</div>
						) : (
							/* MSD Finance Configuration */
							<div className="space-y-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">MSD Client ID *</label>
										<input
											type="text"
											value={formData.config.clientId || ''}
											onChange={(e) => handleConfigChange('clientId', e.target.value)}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
												errors.msdClientId ? 'border-red-300' : 'border-gray-300'
											}`}
											placeholder="Enter MSD Client ID"
										/>
										{errors.msdClientId && <p className="text-red-500 text-sm mt-1">{errors.msdClientId}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">MSD Client Secret *</label>
										<input
											type="text"
											value={formData.config.clientSecret || ''}
											onChange={(e) => handleConfigChange('clientSecret', e.target.value)}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
												errors.msdClientSecret ? 'border-red-300' : 'border-gray-300'
											}`}
											placeholder="Enter MSD Client Secret"
										/>
										{errors.msdClientSecret && <p className="text-red-500 text-sm mt-1">{errors.msdClientSecret}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Tenant ID *</label>
										<input
											type="text"
											value={formData.config.tenantId || ''}
											onChange={(e) => handleConfigChange('tenantId', e.target.value)}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
												errors.msdTenantId ? 'border-red-300' : 'border-gray-300'
											}`}
											placeholder="Enter Tenant ID"
										/>
										{errors.msdTenantId && <p className="text-red-500 text-sm mt-1">{errors.msdTenantId}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Resource URL *</label>
										<input
											type="url"
											value={formData.config.resource || ''}
											onChange={(e) => handleConfigChange('resource', e.target.value)}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
												errors.msdResource ? 'border-red-300' : 'border-gray-300'
											}`}
											placeholder="https://your-instance.dynamics.com"
										/>
										{errors.msdResource && <p className="text-red-500 text-sm mt-1">{errors.msdResource}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Legal Entity (Data Area ID) *
										</label>
										<input
											type="text"
											value={formData.config.dataAreaId || ''}
											onChange={(e) => handleConfigChange('dataAreaId', e.target.value)}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
												errors.msdDataAreaId ? 'border-red-300' : 'border-gray-300'
											}`}
											placeholder="Enter Data Area ID (e.g., qts)"
										/>
										{errors.msdDataAreaId && <p className="text-red-500 text-sm mt-1">{errors.msdDataAreaId}</p>}
									</div>
								</div>

								{/* Service Bus Configuration */}
								<div className="border-t pt-4 mt-4">
									<h4 className="text-md font-medium text-gray-900 mb-3">Service Bus Configuration</h4>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Service Bus Connection String *
											</label>
											<textarea
												value={formData.config.serviceBusConnectionString || ''}
												onChange={(e) => handleConfigChange('serviceBusConnectionString', e.target.value)}
												className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
													errors.msdServiceBusConnectionString ? 'border-red-300' : 'border-gray-300'
												}`}
												placeholder="Endpoint=sb://..."
												rows={2}
											/>
											{errors.msdServiceBusConnectionString && (
												<p className="text-red-500 text-sm mt-1">{errors.msdServiceBusConnectionString}</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Service Bus Queue Name *</label>
											<input
												type="text"
												value={formData.config.serviceBusQueueName || ''}
												onChange={(e) => handleConfigChange('serviceBusQueueName', e.target.value)}
												className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
													errors.msdServiceBusQueueName ? 'border-red-300' : 'border-gray-300'
												}`}
												placeholder="Enter service bus queue name"
											/>
											{errors.msdServiceBusQueueName && (
												<p className="text-red-500 text-sm mt-1">{errors.msdServiceBusQueueName}</p>
											)}
										</div>
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Form Actions */}
					<div className="flex justify-end space-x-3 pt-6 border-t">
						<button
							type="button"
							onClick={onClose}
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500">
							Cancel
						</button>
						<button
							type="submit"
							disabled={loading}
							className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
							{loading ? <RefreshCw className="animate-spin h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
							{client ? 'Update Client' : 'Add Client'}
						</button>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};
